import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AspectsSection = ({ data, isVisible, onToggle }) => {
  const [showMinorAspects, setShowMinorAspects] = useState(false);

  if (!data || data?.length === 0) return null;

  const filteredData = showMinorAspects 
    ? data 
    : data?.filter(aspect => aspect?.isMajor);

  return (
    <section id="aspects" className="scroll-mt-20">
      <div className="bg-card border border-border rounded-lg shadow-brand-md overflow-hidden">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 md:px-6 py-4 hover:bg-muted transition-smooth"
          aria-expanded={isVisible}
        >
          <div className="flex items-center">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center mr-3">
              <Icon name="Triangle" size={20} color="var(--color-success)" />
            </div>
            <div className="text-left">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                มุมระหว่างดาว
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">Aspects</p>
            </div>
          </div>
          <Icon 
            name={isVisible ? "ChevronUp" : "ChevronDown"} 
            size={24} 
            color="var(--color-muted-foreground)"
          />
        </button>

        {isVisible && (
          <div className="border-t border-border">
            {/* Toggle Controls */}
            <div className="p-4 md:p-6 bg-muted/50 border-b border-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-caption font-medium text-foreground">
                    แสดงมุมรอง
                    <span className="block text-xs text-muted-foreground">Show Minor Aspects</span>
                  </p>
                </div>
                <Button
                  variant={showMinorAspects ? "default" : "outline"}
                  size="sm"
                  iconName={showMinorAspects ? "Eye" : "EyeOff"}
                  iconPosition="left"
                  onClick={() => setShowMinorAspects(!showMinorAspects)}
                >
                  {showMinorAspects ? 'แสดงทั้งหมด (All)' : 'มุมหลักเท่านั้น (Major Only)'}
                </Button>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                      ดาวเคราะห์ A
                      <span className="block text-xs text-muted-foreground normal-case">Planet A</span>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                      ประเภทมุม
                      <span className="block text-xs text-muted-foreground normal-case">Aspect Type</span>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                      ดาวเคราะห์ B
                      <span className="block text-xs text-muted-foreground normal-case">Planet B</span>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                      ค่าเบี่ยงเบน
                      <span className="block text-xs text-muted-foreground normal-case">Orb</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredData?.map((aspect, index) => (
                    <tr key={index} className="hover:bg-muted/50 transition-smooth">
                      <td className="px-6 py-4 text-sm font-medium text-foreground">
                        {aspect?.planetA}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        <div className="flex items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            aspect?.isMajor 
                              ? 'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
                          }`}>
                            {aspect?.aspectTypeTh}
                          </span>
                          <span className="ml-2 text-xs text-muted-foreground">{aspect?.aspectTypeEn}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-foreground">
                        {aspect?.planetB}
                      </td>
                      <td className="px-6 py-4 text-sm data-text text-foreground">
                        {aspect?.orb}°
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-border">
              {filteredData?.map((aspect, index) => (
                <div key={index} className="p-4 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-medium text-foreground">
                        {aspect?.planetA} → {aspect?.planetB}
                      </h3>
                      <p className="text-sm text-muted-foreground">Planet Pair</p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      aspect?.isMajor 
                        ? 'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
                    }`}>
                      {aspect?.isMajor ? 'หลัก' : 'รอง'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">ประเภทมุม</p>
                      <p className="text-sm text-foreground">{aspect?.aspectTypeTh}</p>
                      <p className="text-xs text-muted-foreground">{aspect?.aspectTypeEn}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">ค่าเบี่ยงเบน</p>
                      <p className="text-sm data-text text-foreground">{aspect?.orb}°</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredData?.length === 0 && (
              <div className="p-8 text-center">
                <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">ไม่พบมุมระหว่างดาว</p>
                <p className="text-xs text-muted-foreground">No aspects found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default AspectsSection;